
// Charac. IDs
export const LVL_ID = 5;
export const AP_ID = 1;
export const MP_ID = 23;
export const RANGE_ID = 19;
export const SP_ID = 26;
export const VITALITY_ID = 11;
export const WISDOM_ID = 12;
export const STRENGTH_ID = 10;
export const INTELLIGENCE_ID = 15;
export const CHANCE_ID = 13;
export const AGILITY_ID = 14;
export const POWER_ID = 25;
/**
 * Puissance (armes) — Power that only weapon hits get to use.
 *
 * No item in the game carries it: it arrives from a boost the player casts on
 * themselves (`core/boosts.js`), which is why it is not one of the solver's
 * characteristics and never appears as a generator criterion. Weapon damage
 * adds it to Power; spell damage does not (see `calcWeaponVal` in spellUtils).
 */
export const WEAPON_POWER_ID = 103;
export const DAMAGE_ID = 16;
/** Generic fixed damage — folded into elemental damage rows in generator UI. */
export const STAT_GENERATOR_HIDDEN_IDS = [DAMAGE_ID];
export const isStatHiddenInGenerator = (statId) => STAT_GENERATOR_HIDDEN_IDS.includes(statId);
export const INITIATIVE_ID = 44;
export const AP_REDUCTION_ID = 82;
export const MP_REDUCTION_ID = 83;
export const AP_PARRY_ID = 27;
export const MP_PARRY_ID = 28;
export const DODGE_ID = 78;
export const LOCK_ID = 79;
export const CRIT_ID = 18;
export const CRIT_DAMAGE_ID = 86;
/** % Dommages sorts — final multiplier on spell damage (in spellUtils). */
export const SPELL_PER_DMG_ID = 123;
export const FINAL_DAMAGE_ID = 150;
export const MELEE_PER_DMG_ID = 125;
export const RANGED_PER_DMG_ID = 120;

// The generator's None/Mêlée/Distance toggle (genConfig.position): which of
// % Melee / % Ranged a hit takes. "none" declares no side, so neither counts.
// Mirror of score::Position in solver-core.
export const POSITION_NONE = 'none';
export const POSITION_MELEE = 'melee';
export const POSITION_RANGED = 'ranged';
export const POSITIONS = [POSITION_NONE, POSITION_MELEE, POSITION_RANGED];
export const POSITION_DAMAGE_ID = {
    [POSITION_MELEE]: MELEE_PER_DMG_ID,
    [POSITION_RANGED]: RANGED_PER_DMG_ID,
};
export const PROSPECTING_ID = 48;
export const PODS_ID = 40;

/** How a percent stat's label names its unit when the label stands alone ("% Critique"). */
const PERCENT_LABEL_PREFIX = '% ';

/**
 * Ceilings the game puts on a character's characteristics, whatever the gear
 * adds up to. The sheet shows the capped value, with the shortfall as its own
 * breakdown row. Scoring has its own list (SOFT_CAPS in scoreCalculation.js,
 * pinned to the backend), which also caps summons.
 */
export const GAME_STAT_CAPS = {
    [AP_ID]: 12,
    [MP_ID]: 6,
    [RANGE_ID]: 6,
    37: 50, // % Neutral resistance
    33: 50, // % Earth resistance
    34: 50, // % Fire resistance
    35: 50, // % Water resistance
    36: 50, // % Air resistance
};

// Base character stats
export const ATTRIBUTES = [
    VITALITY_ID,
    WISDOM_ID,
    STRENGTH_ID,
    INTELLIGENCE_ID,
    CHANCE_ID,
    AGILITY_ID,
];

export const STATS = [
    { name: "lvl", id: LVL_ID, label: { fr: "Niveau", en: "Level", de: "Stufe", es: "Nivel", pt: "Nível" }, alias: { fr: "niv.", en: "lvl.", de: "Stufe", es: "niv.", pt: "nív." }, unit: "", minRange: 1, maxRange: 200, step: 1, category: "lvl", category_name: { fr: "Niveau", en: "Level", de: "Stufe", es: "Nivel", pt: "Nível" }, icon: "/img/lv.png", order: 0, weight: 1 },
    { name: "pa", id: AP_ID, label: { fr: "PA", en: "AP", de: "AP", es: "PA", pt: "PA" }, alias: { fr: "PA", en: "AP", de: "AP", es: "PA", pt: "PA" }, unit: "", minRange: 6, maxRange: 12, step: 1, category: "primary", category_name: { fr: "Stats primaires", en: "Primary", de: "Primäre Stats", es: "Stats primarias", pt: "Stats primárias" }, exo: true, icon: "/img/stat_pa.png", order: 9, weight: 100 },
    { name: "pm", id: MP_ID, label: { fr: "PM", en: "MP", de: "BP", es: "PM", pt: "PM" }, alias: { fr: "PM", en: "MP", de: "BP", es: "PM", pt: "PM" }, unit: "", minRange: 3, maxRange: 6, step: 1, category: "primary", category_name: { fr: "Stats primaires", en: "Primary", de: "Primäre Stats", es: "Stats primarias", pt: "Stats primárias" }, exo: true, icon: "/img/stat_pm.png", order: 10, weight: 90 },
    { name: "po", id: RANGE_ID, label: { fr: "Portée", en: "Range", de: "Reichweite", es: "Alcance", pt: "Alcance" }, alias: { fr: "PO", en: "Range", de: "RW", es: "AL", pt: "AL" }, unit: "", minRange: 0, maxRange: 6, step: 1, category: "primary", category_name: { fr: "Stats primaires", en: "Primary", de: "Primäre Stats", es: "Stats primarias", pt: "Stats primárias" }, exo: true, icon: "/img/stat_po.png", order: 11, weight: 51 },
    { name: "inv", id: SP_ID, label: { fr: "Invocation", en: "Summons", de: "Beschwörung", es: "Invocación", pt: "Invocação" }, alias: { fr: "PI", en: "SP", de: "BSP", es: "PI", pt: "PI" }, unit: "", minRange: 1, maxRange: 6, step: 1, category: "primary", category_name: { fr: "Stats primaires", en: "Primary", de: "Primäre Stats", es: "Stats primarias", pt: "Stats primárias" }, icon: "/img/stat_invocation.png", order: 12, weight: 30 },
    { name: "ini", id: INITIATIVE_ID, label: { fr: "Initiative", en: "Initiative", de: "Initiative", es: "Iniciativa", pt: "Iniciativa" }, alias: { fr: "initiative", en: "initiative", de: "Initiative", es: "iniciativa", pt: "iniciativa" }, unit: "", minRange: 0, maxRange: 8000, step: 100, category: "secondary", category_name: { fr: "Stats secondaires", en: "Secondary", de: "Sekundäre Stats", es: "Stats secundarias", pt: "Stats secundárias" }, icon: "/img/stat_initiative.png", order: 25, weight: 0.1 },
    { name: "cri", id: CRIT_ID, label: { fr: "% Critique", en: "% Critical", de: "% Kritisch", es: "% Crítico", pt: "% Crítico" }, alias: { fr: "% critique", en: "% critical", de: "% kritisch", es: "% crítico", pt: "% crítico" }, unit: "%", minRange: 0, maxRange: 100, step: 5, category: "secondary", category_name: { fr: "Stats secondaires", en: "Secondary", de: "Sekundäre Stats", es: "Stats secundarias", pt: "Stats secundárias" }, icon: "/img/stat_critique.png", order: 8, weight: 10 },
    { name: "pp", id: PROSPECTING_ID, label: { fr: "Prospection", en: "Prospecting", de: "Filzkunde", es: "Prospección", pt: "Prospecção" }, alias: { fr: "prospection", en: "prospecting", de: "Filzkunde", es: "prospección", pt: "prospecção" }, unit: "", minRange: 100, maxRange: 500, step: 10, category: "secondary", category_name: { fr: "Stats secondaires", en: "Secondary", de: "Sekundäre Stats", es: "Stats secundarias", pt: "Stats secundárias" }, icon: "/img/stat_pp.png", order: 24, weight: 3 },
    { name: "so", id: 49, label: { fr: "Soin", en: "Heal", de: "Heilung", es: "Cura", pt: "Cura" }, alias: { fr: "soin", en: "heal", de: "Heilung", es: "cura", pt: "cura" }, unit: "", minRange: 0, maxRange: 200, step: 5, category: "secondary", category_name: { fr: "Stats secondaires", en: "Secondary", de: "Sekundäre Stats", es: "Stats secundarias", pt: "Stats secundárias" }, icon: "/img/stat_soin.png", order: 23, weight: 4 },
    { name: "pods", id: PODS_ID, label: { fr: "Pods", en: "Pods", de: "Pods", es: "Pods", pt: "Pods" }, alias: { fr: "pods", en: "pods", de: "Pods", es: "pods", pt: "pods" }, unit: "", minRange: 1000, maxRange: 8000, step: 100, category: "secondary", category_name: { fr: "Stats secondaires", en: "Secondary", de: "Sekundäre Stats", es: "Stats secundarias", pt: "Stats secundárias" }, icon: "/img/stat_pods.png", order: 22, weight: 2.5 },
    { name: "vi", id: VITALITY_ID, label: { fr: "Vitalité", en: "Vitality", de: "Vitalität", es: "Vitalidad", pt: "Vitalidade" }, alias: { fr: "Vitalité", en: "Vitality", de: "Vitalität", es: "Vitalidad", pt: "Vitalidade" }, unit: "", minRange: 0, maxRange: 7000, step: 50, category: "attribute", category_name: { fr: "Attributs", en: "Attributes", de: "Attribute", es: "Atributos", pt: "Atributos" }, icon: "/img/stat_vitalite.png", order: 1, weight: 0.38 },
    { name: "sa", id: WISDOM_ID, label: { fr: "Sagesse", en: "Wisdom", de: "Weisheit", es: "Sabiduría", pt: "Sabedoria" }, alias: { fr: "sagesse", en: "wisdom", de: "Weisheit", es: "sabiduría", pt: "sabedoria" }, unit: "", minRange: 0, maxRange: 1000, step: 50, category: "attribute", category_name: { fr: "Attributs", en: "Attributes", de: "Attribute", es: "Atributos", pt: "Atributos" }, icon: "/img/stat_sagesse.png", order: 6, weight: 3 },
    { name: "fo", id: STRENGTH_ID, label: { fr: "Force", en: "Strength", de: "Stärke", es: "Fuerza", pt: "Força" }, alias: { fr: "force", en: "strength", de: "Stärke", es: "fuerza", pt: "força" }, unit: "", minRange: 0, maxRange: 2000, step: 50, category: "attribute", category_name: { fr: "Attributs", en: "Attributes", de: "Attribute", es: "Atributos", pt: "Atributos" }, icon: "/img/stat_force.png", order: 2, weight: 1.5 },
    { name: "ine", id: INTELLIGENCE_ID, label: { fr: "Intelligence", en: "Intelligence", de: "Intelligenz", es: "Inteligencia", pt: "Inteligência" }, alias: { fr: "intelligence", en: "intelligence", de: "Intelligenz", es: "inteligencia", pt: "inteligência" }, unit: "", minRange: 0, maxRange: 2000, step: 50, category: "attribute", category_name: { fr: "Attributs", en: "Attributes", de: "Attribute", es: "Atributos", pt: "Atributos" }, icon: "/img/stat_intelligence.png", order: 3, weight: 1.5 },
    { name: "cha", id: CHANCE_ID, label: { fr: "Chance", en: "Chance", de: "Glück", es: "Suerte", pt: "Sorte" }, alias: { fr: "chance", en: "chance", de: "Glück", es: "suerte", pt: "sorte" }, unit: "", minRange: 0, maxRange: 2000, step: 50, category: "attribute", category_name: { fr: "Attributs", en: "Attributes", de: "Attribute", es: "Atributos", pt: "Atributos" }, icon: "/img/stat_chance.png", order: 4, weight: 1.5 },
    { name: "age", id: AGILITY_ID, label: { fr: "Agilité", en: "Agility", de: "Flinkheit", es: "Agilidad", pt: "Agilidade" }, alias: { fr: "agilité", en: "agility", de: "Flinkheit", es: "agilidad", pt: "agilidade" }, unit: "", minRange: 0, maxRange: 2000, step: 50, category: "attribute", category_name: { fr: "Attributs", en: "Attributes", de: "Attribute", es: "Atributos", pt: "Atributos" }, icon: "/img/stat_agilite.png", order: 5, weight: 1.5 },
    { name: "pui", id: POWER_ID, label: { fr: "Puissance", en: "Power", de: "Schlagkraft", es: "Potencia", pt: "Potência" }, alias: { fr: "puissance", en: "power", de: "Schlagkraft", es: "potencia", pt: "potência" }, unit: "", minRange: 0, maxRange: 2000, step: 50, category: "attribute", category_name: { fr: "Attributs", en: "Attributes", de: "Attribute", es: "Atributos", pt: "Atributos" }, icon: "/img/stat_puissance.png", alt: "/img/stat_multi.png", order: 7, weight: 2 },
    { name: "fui", id: DODGE_ID, label: { fr: "Fuite", en: "Dodge", de: "Ausweichen", es: "Huida", pt: "Fuga" }, alias: { fr: "fuite", en: "dodge", de: "Ausweichen", es: "huida", pt: "fuga" }, unit: "", minRange: 0, maxRange: 200, step: 5, category: "entrave", category_name: { fr: "Entrave", en: "Control", de: "Behinderung", es: "Control", pt: "Controle" }, icon: "/img/stat_fuite.png", order: 27, weight: 4 },
    { name: "tac", id: LOCK_ID, label: { fr: "Tacle", en: "Lock", de: "Blocken", es: "Placaje", pt: "Bloqueio" }, alias: { fr: "tacle", en: "lock", de: "Blocken", es: "placaje", pt: "bloqueio" }, unit: "", minRange: 0, maxRange: 200, step: 5, category: "entrave", category_name: { fr: "Entrave", en: "Control", de: "Behinderung", es: "Control", pt: "Controle" }, icon: "/img/stat_tacle.png", order: 26, weight: 4 },
    { name: "re_pa", id: AP_PARRY_ID, label: { fr: "Esquive P.A.", en: "A.P. parry", de: "Res. gg. AP-Entzug", es: "Esquiva PA", pt: "Esquiva PA" }, alias: { fr: "esq. PA", en: "AP parry", de: "AP-Res.", es: "esq. PA", pt: "esq. PA" }, unit: "", minRange: 0, maxRange: 300, step: 5, category: "entrave", category_name: { fr: "Entrave", en: "Control", de: "Behinderung", es: "Control", pt: "Controle" }, icon: "/img/stat_esq_pa.png", order: 30, weight: 4 },
    { name: "ret_pa", id: AP_REDUCTION_ID, label: { fr: "Retrait P.A.", en: "A.P. reduction", de: "AP-Entzug", es: "Retiro de PA", pt: "Retirada de PA" }, alias: { fr: "ret. PA", en: "AP red.", de: "AP-Entz.", es: "ret. PA", pt: "ret. PA" }, unit: "", minRange: 0, maxRange: 300, step: 5, category: "entrave", category_name: { fr: "Entrave", en: "Control", de: "Behinderung", es: "Control", pt: "Controle" }, icon: "/img/stat_ret_pa.png", order: 28, weight: 4 },
    { name: "re_pme", id: MP_PARRY_ID, label: { fr: "Esquive PM", en: "M.P. parry", de: "Res. gg. BP-Entzug", es: "Esquiva PM", pt: "Esquiva PM" }, alias: { fr: "esq. PM", en: "MP parry", de: "BP-Res.", es: "esq. PM", pt: "esq. PM" }, unit: "", minRange: 0, maxRange: 300, step: 5, category: "entrave", category_name: { fr: "Entrave", en: "Control", de: "Behinderung", es: "Control", pt: "Controle" }, icon: "/img/stat_esq_pm.png", order: 31, weight: 4 },
    { name: "ret_pme", id: MP_REDUCTION_ID, label: { fr: "Retrait P.M.", en: "M.P. reduction", de: "BP-Entzug", es: "Retiro de PM", pt: "Retirada de PM" }, alias: { fr: "ret. PM", en: "MP red.", de: "BP-Entz.", es: "ret. PM", pt: "ret. PM" }, unit: "", minRange: 0, maxRange: 300, step: 5, category: "entrave", category_name: { fr: "Entrave", en: "Control", de: "Behinderung", es: "Control", pt: "Controle" }, icon: "/img/stat_ret_pm.png", order: 29, weight: 4 },
    { name: "do_neu", id: 92, label: { fr: "Dommages neutre", en: "Neutral damage", de: "Neutraler Schaden", es: "Daños neutrales", pt: "Danos neutros" }, alias: { fr: "do. neutre", en: "neutral", de: "neutral", es: "neutral", pt: "neutro" }, unit: "", minRange: 0, maxRange: 300, step: 5, category: "damage_fixed", category_name: { fr: "Dommages fixes", en: "Fixed damage", de: "Fester Schaden", es: "Daños fijos", pt: "Danos fixos" }, icon: "/img/stat_do_neutre.png", order: 14, weight: 5 },
    { name: "do_ter", id: 88, label: { fr: "Dommages terre", en: "Earth damage", de: "Erdschaden", es: "Daños de tierra", pt: "Danos de terra" }, alias: { fr: "do. terre", en: "earth", de: "Erde", es: "tierra", pt: "terra" }, unit: "", minRange: 0, maxRange: 300, step: 5, category: "damage_fixed", category_name: { fr: "Dommages fixes", en: "Fixed damage", de: "Fester Schaden", es: "Daños fijos", pt: "Danos fixos" }, icon: "/img/stat_do_terre.png", order: 15, weight: 5 },
    { name: "do_feu", id: 89, label: { fr: "Dommages feu", en: "Fire damage", de: "Feuerschaden", es: "Daños de fuego", pt: "Danos de fogo" }, alias: { fr: "do. feu", en: "fire", de: "Feuer", es: "fuego", pt: "fogo" }, unit: "", minRange: 0, maxRange: 300, step: 5, category: "damage_fixed", category_name: { fr: "Dommages fixes", en: "Fixed damage", de: "Fester Schaden", es: "Daños fijos", pt: "Danos fixos" }, icon: "/img/stat_do_feu.png", order: 16, weight: 5 },
    { name: "do_eau", id: 90, label: { fr: "Dommages eau", en: "Water damage", de: "Wasserschaden", es: "Daños de agua", pt: "Danos de água" }, alias: { fr: "do. eau", en: "water", de: "Wasser", es: "agua", pt: "água" }, unit: "", minRange: 0, maxRange: 300, step: 5, category: "damage_fixed", category_name: { fr: "Dommages fixes", en: "Fixed damage", de: "Fester Schaden", es: "Daños fijos", pt: "Danos fixos" }, icon: "/img/stat_do_eau.png", order: 17, weight: 5 },
    { name: "do_air", id: 91, label: { fr: "Dommages air", en: "Air damage", de: "Luftschaden", es: "Daños de aire", pt: "Danos de ar" }, alias: { fr: "do. air", en: "air", de: "Luft", es: "aire", pt: "ar" }, unit: "", minRange: 0, maxRange: 300, step: 5, category: "damage_fixed", category_name: { fr: "Dommages fixes", en: "Fixed damage", de: "Fester Schaden", es: "Daños fijos", pt: "Danos fixos" }, icon: "/img/stat_do_air.png", order: 18, weight: 5 },
    { name: "do", id: 16, label: { fr: "Dommages", en: "Damage", de: "Schaden", es: "Daños", pt: "Danos" }, alias: { fr: "dommages", en: "damage", de: "Schaden", es: "daños", pt: "danos" }, unit: "", minRange: 0, maxRange: 300, step: 5, category: "damage_fixed", category_name: { fr: "Dommages fixes", en: "Fixed damage", de: "Fester Schaden", es: "Daños fijos", pt: "Danos fixos" }, icon: "/img/stat_do.png", order: 13, weight: 20 },
    { name: "do_cri", id: CRIT_DAMAGE_ID, label: { fr: "Dommages critique", en: "Critical damage", de: "Kritischer Schaden", es: "Daños críticos", pt: "Danos críticos" }, alias: { fr: "do. crit.", en: "critical", de: "krit.", es: "crít.", pt: "crít." }, unit: "", minRange: 0, maxRange: 300, step: 5, category: "damage_fixed", category_name: { fr: "Dommages fixes", en: "Fixed damage", de: "Fester Schaden", es: "Daños fijos", pt: "Danos fixos" }, icon: "/img/stat_do_critique.png", order: 32, weight: 5 },
    { name: "do_pou", id: 84, label: { fr: "Dommages poussée", en: "Push damage", de: "Schubsschaden", es: "Daños por empuje", pt: "Danos de empurrão" }, alias: { fr: "do. pou.", en: "pushback", de: "Schubs", es: "empuje", pt: "empurrão" }, unit: "", minRange: 0, maxRange: 600, step: 5, category: "damage_fixed", category_name: { fr: "Dommages fixes", en: "Fixed damage", de: "Fester Schaden", es: "Daños fijos", pt: "Danos fixos" }, icon: "/img/stat_do_poussee.png", order: 33, weight: 5 },
    { name: "re_neu", id: 58, label: { fr: "Résistance neutre", en: "Neutral resistance", de: "Neutral-Resistenz", es: "Resistencia neutral", pt: "Resistência neutra" }, alias: { fr: "ré. neutre", en: "neutral res.", de: "Neutral-Res.", es: "res. neutral", pt: "res. neutra" }, unit: "", minRange: 0, maxRange: 200, step: 5, category: "resistance_fixed", category_name: { fr: "Résistances fixes", en: "Fixed Resistances", de: "Feste Resistenzen", es: "Resistencias fijas", pt: "Resistências fixas" }, icon: "/img/stat_re_neutre.png", order: 44, weight: 2 },
    { name: "re_ter", id: 54, label: { fr: "Résistance terre", en: "Earth resistance", de: "Erd-Resistenz", es: "Resistencia de tierra", pt: "Resistência de terra" }, alias: { fr: "ré. terre", en: "earth res.", de: "Erd-Res.", es: "res. tierra", pt: "res. terra" }, unit: "", minRange: 0, maxRange: 200, step: 5, category: "resistance_fixed", category_name: { fr: "Résistances fixes", en: "Fixed Resistances", de: "Feste Resistenzen", es: "Resistencias fijas", pt: "Resistências fixas" }, icon: "/img/stat_re_terre.png", order: 45, weight: 2 },
    { name: "re_feu", id: 55, label: { fr: "Résistance feu", en: "Fire resistance", de: "Feuer-Resistenz", es: "Resistencia de fuego", pt: "Resistência de fogo" }, alias: { fr: "ré. feu", en: "fire res.", de: "Feuer-Res.", es: "res. fuego", pt: "res. fogo" }, unit: "", minRange: 0, maxRange: 200, step: 5, category: "resistance_fixed", category_name: { fr: "Résistances fixes", en: "Fixed Resistances", de: "Feste Resistenzen", es: "Resistencias fijas", pt: "Resistências fixas" }, icon: "/img/stat_re_feu.png", order: 46, weight: 2 },
    { name: "re_eau", id: 56, label: { fr: "Résistance eau", en: "Water resistance", de: "Wasser-Resistenz", es: "Resistencia de agua", pt: "Resistência de água" }, alias: { fr: "ré. eau", en: "water res.", de: "Wasser-Res.", es: "res. agua", pt: "res. água" }, unit: "", minRange: 0, maxRange: 200, step: 5, category: "resistance_fixed", category_name: { fr: "Résistances fixes", en: "Fixed Resistances", de: "Feste Resistenzen", es: "Resistencias fijas", pt: "Resistências fixas" }, icon: "/img/stat_re_eau.png", order: 47, weight: 2 },
    { name: "re_air", id: 57, label: { fr: "Résistance air", en: "Air resistance", de: "Luft-Resistenz", es: "Resistencia de aire", pt: "Resistência de ar" }, alias: { fr: "ré. air", en: "air res.", de: "Luft-Res.", es: "res. aire", pt: "res. ar" }, unit: "", minRange: 0, maxRange: 200, step: 5, category: "resistance_fixed", category_name: { fr: "Résistances fixes", en: "Fixed Resistances", de: "Feste Resistenzen", es: "Resistencias fijas", pt: "Resistências fixas" }, icon: "/img/stat_re_air.png", order: 48, weight: 2 },
    { name: "re_cri", id: 87, label: { fr: "Résistance critique", en: "Critical resistance", de: "Kritisch-Resistenz", es: "Resistencia crítica", pt: "Resistência crítica" }, alias: { fr: "ré. crit.", en: "crit. res.", de: "krit. Res.", es: "res. crít.", pt: "res. crít." }, unit: "", minRange: 0, maxRange: 500, step: 5, category: "resistance_fixed", category_name: { fr: "Résistances fixes", en: "Fixed Resistances", de: "Feste Resistenzen", es: "Resistencias fijas", pt: "Resistências fixas" }, icon: "/img/stat_re_critique.png", order: 34, weight: 2 },
    { name: "re_pou", id: 85, label: { fr: "Résistance poussée", en: "Push resistance", de: "Schubs-Resistenz", es: "Resistencia a empuje", pt: "Resistência a empurrão" }, alias: { fr: "ré. pou.", en: "push. res.", de: "Schubs-Res.", es: "res. empuje", pt: "res. empurrão" }, unit: "", minRange: 0, maxRange: 500, step: 5, category: "resistance_fixed", category_name: { fr: "Résistances fixes", en: "Fixed Resistances", de: "Feste Resistenzen", es: "Resistencias fijas", pt: "Resistências fixas" }, icon: "/img/stat_re_poussee.png", order: 35, weight: 2 },
    { name: "do_per_so", id: SPELL_PER_DMG_ID, label: { fr: "% Dommages sorts", en: "% Spells damage", de: "% Zauberschaden", es: "% Daños de hechizos", pt: "% Danos de feitiços" }, alias: { fr: "% sorts", en: "% spell", de: "% Zauber", es: "% hechizos", pt: "% feitiços" }, unit: "%", minRange: 0, maxRange: 30, step: 1, category: "damage_final", category_name: { fr: "Dommages %", en: "Damage %", de: "Schaden %", es: "Daños %", pt: "Danos %" }, icon: "/img/stat_per_do_sorts.png", order: 19, weight: 15 },
    { name: "do_per_me", id: 125, label: { fr: "% Dommages mélée", en: "% Melee damage", de: "% Nahkampfschaden", es: "% Daños c. a c.", pt: "% Danos corpo a corpo" }, alias: { fr: "% mêlée", en: "% melee", de: "% Nahkampf", es: "% c. a c.", pt: "% corpo a corpo" }, unit: "%", minRange: 0, maxRange: 30, step: 1, category: "damage_final", category_name: { fr: "Dommages %", en: "Damage %", de: "Schaden %", es: "Daños %", pt: "Danos %" }, icon: "/img/stat_per_do_melee.png", order: 20, weight: 15 },
    { name: "do_per_di", id: 120, label: { fr: "% Dommages dist", en: "% Ranged damage", de: "% Fernkampfschaden", es: "% Daños a distancia", pt: "% Danos à distância" }, alias: { fr: "% distance", en: "% range", de: "% Fernkampf", es: "% distancia", pt: "% distância" }, unit: "%", minRange: 0, maxRange: 30, step: 1, category: "damage_final", category_name: { fr: "Dommages %", en: "Damage %", de: "Schaden %", es: "Daños %", pt: "Danos %" }, icon: "/img/stat_per_do_dist.png", order: 21, weight: 15 },
    { name: "do_per_ar", id: 122, label: { fr: "% Dommages armes", en: "% Weapons damage", de: "% Waffenschaden", es: "% Daños de armas", pt: "% Danos de armas" }, alias: { fr: "% dom. armes ", en: "% weapons dam.", de: "% Waffenschaden ", es: "% daños armas ", pt: "% danos armas " }, unit: "%", minRange: 0, maxRange: 30, step: 1, category: "other", category_name: { fr: "Autres", en: "Other", de: "Sonstige", es: "Otros", pt: "Outros" }, icon: "/img/stat_per_do_armes.png", order: 22, weight: 15 },
    // Not a panel row (like its `other` siblings above): no item grants it, so a
    // desired value here would be a criterion the generator could never meet. It
    // exists so a boost has somewhere to land and weapon damage somewhere to read.
    { name: "pui_ar", id: WEAPON_POWER_ID, label: { fr: "Puissance (armes)", en: "Power (weapons)", de: "Schlagkraft (Waffen)", es: "Potencia (armas)", pt: "Potência (armas)" }, alias: { fr: "pui. armes", en: "power (wpn)", de: "Schlagkr. (W.)", es: "pot. armas", pt: "pot. armas" }, unit: "", minRange: 0, maxRange: 400, step: 50, category: "other", category_name: { fr: "Autres", en: "Other", de: "Sonstige", es: "Otros", pt: "Outros" }, icon: "/img/stat_puissance.png", order: 49, weight: 2 },
    { name: "do_per_final", id: FINAL_DAMAGE_ID, label: { fr: "% Dommages finaux", en: "% Final damage", de: "% Endschaden", es: "% Daños finales", pt: "% Danos finais" }, alias: { fr: "% dom. finaux", en: "% final dam.", de: "% Endschaden", es: "% daños finales", pt: "% danos finais" }, unit: "%", minRange: 0, maxRange: 30, step: 1, category: "other", category_name: { fr: "Autres", en: "Other", de: "Sonstige", es: "Otros", pt: "Outros" }, icon: "/img/stat_dommages.png", order: 23, weight: 15 },
    { name: "re_per_neu", id: 37, label: { fr: "% Résistance Neutre", en: "% Neutral resistance", de: "% Neutral-Resistenz", es: "% Resistencia neutral", pt: "% Resistência neutra" }, alias: { fr: "% ré. neutre", en: "% neutral res.", de: "% Neutral-Res.", es: "% res. neutral", pt: "% res. neutra" }, unit: "%", minRange: 0, maxRange: 50, step: 1, category: "resistance", category_name: { fr: "Résistances %", en: "Resistances %", de: "Resistenzen %", es: "Resistencias %", pt: "Resistências %" }, icon: "/img/stat_re_neutre.png", order: 36, weight: 6 },
    { name: "re_per_ter", id: 33, label: { fr: "% Résistance Terre", en: "% Earth resistance", de: "% Erd-Resistenz", es: "% Resistencia de tierra", pt: "% Resistência de terra" }, alias: { fr: "% ré. terre", en: "% earth res.", de: "% Erd-Res.", es: "% res. tierra", pt: "% res. terra" }, unit: "%", minRange: 0, maxRange: 50, step: 1, category: "resistance", category_name: { fr: "Résistances %", en: "Resistances %", de: "Resistenzen %", es: "Resistencias %", pt: "Resistências %" }, icon: "/img/stat_re_terre.png", order: 37, weight: 10 },
    { name: "re_per_feu", id: 34, label: { fr: "% Résistance Feu", en: "% Fire resistance", de: "% Feuer-Resistenz", es: "% Resistencia de fuego", pt: "% Resistência de fogo" }, alias: { fr: "% ré. feu", en: "% fire res.", de: "% Feuer-Res.", es: "% res. fuego", pt: "% res. fogo" }, unit: "%", minRange: 0, maxRange: 50, step: 1, category: "resistance", category_name: { fr: "Résistances %", en: "Resistances %", de: "Resistenzen %", es: "Resistencias %", pt: "Resistências %" }, icon: "/img/stat_re_feu.png", order: 38, weight: 10 },
    { name: "re_per_eau", id: 35, label: { fr: "% Résistance Eau", en: "% Water resistance", de: "% Wasser-Resistenz", es: "% Resistencia de agua", pt: "% Resistência de água" }, alias: { fr: "% ré. eau", en: "% water res.", de: "% Wasser-Res.", es: "% res. agua", pt: "% res. água" }, unit: "%", minRange: 0, maxRange: 50, step: 1, category: "resistance", category_name: { fr: "Résistances %", en: "Resistances %", de: "Resistenzen %", es: "Resistencias %", pt: "Resistências %" }, icon: "/img/stat_re_eau.png", order: 39, weight: 10 },
    { name: "re_per_air", id: 36, label: { fr: "% Résistance Air", en: "% Air resistance", de: "% Luft-Resistenz", es: "% Resistencia de aire", pt: "% Resistência de ar" }, alias: { fr: "% ré. air", en: "% air res.", de: "% Luft-Res.", es: "% res. aire", pt: "% res. ar" }, unit: "%", minRange: 0, maxRange: 50, step: 1, category: "resistance", category_name: { fr: "Résistances %", en: "Resistances %", de: "Resistenzen %", es: "Resistencias %", pt: "Resistências %" }, icon: "/img/stat_re_air.png", order: 40, weight: 10 },
    { name: "re_per_me", id: 124, label: { fr: "% Résistance mélée", en: "% Melee resistance", de: "% Nahkampf-Resistenz", es: "% Resistencia c. a c.", pt: "% Resistência corpo a corpo" }, alias: { fr: "% ré. mêlée", en: "% melee res.", de: "% Nahkampf-Res.", es: "% res. c. a c.", pt: "% res. corpo a corpo" }, unit: "%", minRange: 0, maxRange: 30, step: 1, category: "resistance", category_name: { fr: "Résistances %", en: "Resistances %", de: "Resistenzen %", es: "Resistencias %", pt: "Resistências %" }, icon: "/img/stat_re_melee.png", order: 41, weight: 25 },
    { name: "re_per_di", id: 121, label: { fr: "% Résistance dist", en: "% Ranged resistance", de: "% Fernkampf-Resistenz", es: "% Resistencia a distancia", pt: "% Resistência à distância" }, alias: { fr: "% ré. distance", en: "% range res.", de: "% Fernkampf-Res.", es: "% res. distancia", pt: "% res. distância" }, unit: "%", minRange: 0, maxRange: 30, step: 1, category: "resistance", category_name: { fr: "Résistances %", en: "Resistances %", de: "Resistenzen %", es: "Resistencias %", pt: "Resistências %" }, icon: "/img/stat_re_dist.png", order: 42, weight: 25 },
    { name: "re_per_ar", id: 142, label: { fr: "% Résistance armes", en: "% Weapons resistance", de: "% Waffen-Resistenz", es: "% Resistencia de armas", pt: "% Resistência de armas" }, alias: { fr: "% res. armes ", en: "% weapons res.", de: "% Waffen-Res. ", es: "% res. armas ", pt: "% res. armas " }, unit: "%", minRange: 0, maxRange: 30, step: 1, category: "other", category_name: { fr: "Autres", en: "Other", de: "Sonstige", es: "Otros", pt: "Outros" }, icon: "/img/stat_re_armes.png", order: 43, weight: 15 },
];

// charId → default weight; must match Characs enum weights in core/enums/characteristics.py
export const STAT_WEIGHTS_MAP = {
    ...Object.fromEntries(STATS.map((s) => [Number(s.id), s.weight])),
    141: 25,  // Spell Res % (not in STATS display list)
    50: 10,   // Damage Reflection (not in STATS display list)
};

export const STAT_CATEGORY_ORDER = [
    "primary",
    "attribute",
    "secondary",
    "damage_final",
    "damage_fixed",
    "entrave",
    "resistance",
    "resistance_fixed",
];

/** Flat category lists — original two-column desktop panel. */
export const STAT_PANEL_CONFIG_LEGACY = {
    left: [
        "primary",
        "attribute",
        "secondary",
        "entrave",
    ],
    right: [
        "damage_fixed",
        "damage_final",
        "resistance",
        "resistance_fixed",
    ],
};

/** 'legacy' | 'mobile' — which desktop panel layout BuildStats renders. */
export const STAT_PANEL_LAYOUT_VARIANT = 'mobile';

export const STAT_COMPACT_CATEGORY_NAME_OVERRIDES = {
    damage_fixed: { fr: "Dommages", en: "Damage", de: "Schaden", es: "Daños", pt: "Danos" },
    resistance: { fr: "Résistances", en: "Resistances", de: "Resistenzen", es: "Resistencias", pt: "Resistências" },
};

export const STAT_COMPACT_SHOW_CATEGORY_HEADERS = {
    mobile: false,
    desktop: false,
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPACT STATS LAYOUT — WYSIWYG. The strings below ARE the layout:
// what you type is where it renders. Save → hot reload → glance → repeat.
// Used on mobile (bottom sheet) and desktop-compact (generator off / view-only).
//
//   • Each line   = one visual row. Each `|` starts a new column.
//     Any number of columns and rows — just draw the grid you want.
//   • Blank line  = new section (its own box; desktop masonry flows sections).
//   • `#category` = header cell (uses compact override above, then category name).
//       - alone on its line → single title spanning the whole section
//       - on a `|` line     → per-column headers (e.g. `#primary | #attribute`)
//   • `@N` after a header = desktop masonry column hint (0 = leftmost).
//   • Set STAT_COMPACT_SHOW_CATEGORY_HEADERS.mobile / desktop to false to hide headers.
//   • Empty cell (or `.`) = gap — the row position is kept as blank space.
//   • Stat tokens are the `name` field of STATS. Unknown names are ignored.
//   • Pipes don't need to line up — align them if it helps you read it.
//   • MOBILE and DESKTOP are fully independent: arrange each for its own width.
// ─────────────────────────────────────────────────────────────────────────────

// MOBILE compact layout (bottom sheet — sections stack top to bottom).
export const STAT_COMPACT_MOBILE_LAYOUT = `
#primary   
pa         | po
pm         | inv


#attribute | #secondary
vi         | pui
fo         | cri
ine         | ini
cha        | so
age        | pp
sa        | pods

#entrave
tac        | fui
ret_pa      | re_pa
ret_pme     | re_pme

#damage_fixed
do_neu     | do_cri
do_ter     | do_pou
do_feu     | do_per_so
do_eau     | do_per_me
do_air     | do_per_di

#resistance
re_neu | re_per_neu
re_ter | re_per_ter
re_feu | re_per_feu
re_eau | re_per_eau
re_air | re_per_air
re_cri  | re_per_di
re_pou  |  re_per_me
`;

/** Mobile-ordered desktop panel, using the original two-column desktop grid. */
export const STAT_PANEL_CONFIG = {
    left: [
        { category: "primary", stats: ["pa", "pm"] },
        { category: "attribute", stats: ["vi", "fo", "ine", "cha", "age", "sa"] },
        { category: "entrave", stats: ["tac", "ret_pa", "ret_pme"] },
        { category: "damage_fixed", stats: ["do_neu", "do_ter", "do_feu", "do_eau", "do_air"] },
        { category: "resistance", stats: ["re_neu", "re_ter", "re_feu", "re_eau", "re_air", "re_cri", "re_pou"] },
    ],
    right: [
        { category: "primary", stats: ["po", "inv"] },
        { category: "secondary", stats: ["pui", "cri", "ini", "so", "pp", "pods"] },
        { category: "entrave", stats: ["fui", "re_pa", "re_pme"] },
        { category: "damage_fixed", stats: ["do_cri", "do_pou", "do_per_so", "do_per_me", "do_per_di"] },
        { category: "resistance", stats: ["re_per_neu", "re_per_ter", "re_per_feu", "re_per_eau", "re_per_air", "re_per_di", "re_per_me"] },
    ],
};

export const STAT_COMPACT_DESKTOP_COLUMN_COUNT = 2;

// DESKTOP compact layout (@N = masonry column, wide screens only). On wide
// screens, @0 (character stats) and @1 (damage + resistances) form two equal
// columns; narrower widths ignore the hints and stack every section like mobile.
export const STAT_COMPACT_DESKTOP_LAYOUT = `
#primary @0
pa         | po
pm         | inv


#attribute | #secondary @0
vi         | pui
fo         | cri
ine         | ini
cha        | so
age        | pp
sa        | pods


#entrave @0
tac        | fui
ret_pa      | re_pa
ret_pme     | re_pme

#damage_fixed @1
do_neu      | do_cri
do_ter      | do_pou
do_feu      | do_per_so
do_eau      | do_per_me
do_air      | do_per_di

#resistance @1
re_neu | re_per_neu
re_ter | re_per_ter
re_feu | re_per_feu
re_eau | re_per_eau
re_air | re_per_air
re_cri  | re_per_di
re_pou  |  re_per_me
`;

/** Parse one WYSIWYG layout string into structured sections (see legend above). */
const parseCompactLayout = (layout) => {
    const blocks = String(layout || '')
        .split(/\n[ \t]*\n/)
        .map(block => block.split('\n').map(line => line.trim()).filter(Boolean))
        .filter(lines => lines.length > 0);

    return blocks.map((lines, blockIndex) => {
        let title = null;
        let desktopColumn = null;
        const headerRows = [];
        const statRows = [];

        const parseHeaderCell = (cell) => {
            const match = cell.match(/^#([\w-]+)(?:\s+@(\d+))?$/);
            if (!match) return null;
            if (match[2] != null) desktopColumn = Number(match[2]);
            return match[1];
        };

        for (const line of lines) {
            const cells = line.split('|').map(cell => cell.trim());
            if (cells.some(cell => cell.startsWith('#'))) {
                if (cells.length === 1) {
                    title = parseHeaderCell(cells[0]) ?? title;
                } else {
                    headerRows.push(cells.map(cell => (cell.startsWith('#') ? parseHeaderCell(cell) : null)));
                }
            } else {
                statRows.push(cells);
            }
        }

        const colCount = Math.max(
            1,
            ...statRows.map(row => row.length),
            ...headerRows.map(row => row.length),
        );

        // Transpose rows → columns; blank / '.' cells become null gaps so each
        // stat keeps the exact row you typed it on.
        const columns = Array.from({ length: colCount }, (_, col) =>
            statRows.map(row => {
                const cell = row[col] ?? '';
                return (cell === '' || cell === '.') ? null : cell;
            })
        );

        const columnHeaders = headerRows.length > 0
            ? Array.from({ length: colCount }, (_, col) => headerRows.map(row => row[col]).find(Boolean) || null)
            : null;

        return {
            key: title || columnHeaders?.find(Boolean) || `group-${blockIndex}`,
            title,
            columnHeaders,
            desktopColumn,
            columns,
        };
    });
};

/**
 * Resolve a WYSIWYG layout string into render-ready groups.
 * Output per group: { category, columns: (statObj|null)[][], columnCategories?, desktopColumn }.
 * null entries are gaps (spacer rows); trailing gaps are trimmed; empty columns dropped.
 */
export const resolveCompactPanelGroups = (layout = STAT_COMPACT_DESKTOP_LAYOUT) => (
    parseCompactLayout(layout)
        .map(group => {
            const built = group.columns
                .map((cells, index) => {
                    const stats = cells.map(name => (name && STATS.find(stat => stat.name === name)) || null);
                    while (stats.length > 0 && stats[stats.length - 1] === null) stats.pop();
                    return { header: group.columnHeaders?.[index] || null, stats };
                })
                .filter(col => col.stats.some(Boolean));
            if (built.length === 0) return null;

            const hasColumnHeaders = built.some(col => col.header);
            return {
                category: group.key,
                columns: built.map(col => col.stats),
                ...(hasColumnHeaders ? { columnCategories: built.map(col => col.header) } : {}),
                desktopColumn: group.desktopColumn,
            };
        })
        .filter(Boolean)
);

/** Desktop compact: optional desktopColumn hints; otherwise config array order. */
export const orderCompactPanelGroups = (groups, { useDesktopColumnHints = false } = {}) => {
    if (!useDesktopColumnHints || !groups.some(g => g.desktopColumn != null)) {
        return groups;
    }
    return groups
        .map((group, index) => ({ group, index }))
        .sort((a, b) => {
            const colA = a.group.desktopColumn ?? Number.MAX_SAFE_INTEGER;
            const colB = b.group.desktopColumn ?? Number.MAX_SAFE_INTEGER;
            if (colA !== colB) return colA - colB;
            return a.index - b.index;
        })
        .map(({ group }) => group);
};

/** Bucket ordered groups into desktop masonry columns (0 = leftmost). */
export const groupCompactPanelByDesktopColumn = (
    groups,
    columnCount = STAT_COMPACT_DESKTOP_COLUMN_COUNT,
) => {
    const buckets = Array.from({ length: columnCount }, () => []);
    groups.forEach(group => {
        const col = group.desktopColumn ?? 0;
        const target = Math.min(Math.max(0, col), columnCount - 1);
        buckets[target].push(group);
    });
    return buckets;
};

export const STAT_FILTER_CATEGORIES = {
    left: [
        "primary",
        "attribute",
        "entrave",
    ],
    right: [
        "damage_fixed",
        "damage_final",
        "resistance",
        "resistance_fixed",
    ],
    mergeMap: {
        secondary: "primary",
    },
    // Stats pulled in by name instead of by category. The `other` category is a
    // grab bag that made a poor filter section of its own; only weapon damage %
    // is worth filtering on, and it belongs with the other damage percentages.
    // The rest of `other` is intentionally not offered as a filter.
    extraStats: {
        damage_final: ["do_per_ar"],
    },
};

export const STAT_PANEL_3COLUMN_CONFIG = {
    left: [
        "primary",
        "attribute",
        "secondary",
    ],
    center: [
        "damage_fixed",
        "damage_final",
        "entrave",
    ],
    right: [
        "resistance",
        "resistance_final",
        "resistance_fixed",
        "other"
    ],
};

export const getStatLabel = (statName, lang = 'fr') => {
    return STATS.find(s => s.name === statName)?.label?.[lang] || '';
};

export const getStatAlias = (statName, lang = 'fr') => {
    return STATS.find(s => s.name === statName)?.alias?.[lang] || '';
};

/**
 * A label shown beside its value: the unit moves onto the value ("7%
 * Critique"), so the label drops the "% " it carries when it stands alone.
 */
export const labelBesideValue = (label) => (
    label.startsWith(PERCENT_LABEL_PREFIX) ? label.slice(PERCENT_LABEL_PREFIX.length) : label
);

export const getStatCategory = (category, lang = 'fr') => {
    return STATS.find(s => s.category === category)?.category_name?.[lang] || '';
};

export const getCompactStatCategory = (category, lang = 'fr') => {
    const override = STAT_COMPACT_CATEGORY_NAME_OVERRIDES[category];
    return override?.[lang] || override?.fr || getStatCategory(category, lang);
};

export const getTotalCharacterPoints = (lvl) => {
    return Math.min((lvl - 1) * 5, 995)
}

export const getDefaultDesiredStats = () => ({
    pa: 12,
    pm: 6
});

/**
 * Life points a naked character owns from its level alone (55 at level 1, +5 per level).
 *
 * Our Vitality row is the HP total, base PV included, because that is what the game
 * adds up on the sheet. The game keeps the two apart wherever it reads the *characteristic*
 * only — an equip criterion such as `Vitalité > 3949` (Gargandyas gear) is cleared at
 * 4999 HP on a level 200 character, so anything comparing a criterion against our row
 * has to take this offset back out first. Mirrors `base_vitality()` in
 * backend/core/enums/characteristics.py and `base_vitality()` in solver-core/src/charac.rs.
 */
export const getBaseVitality = (lvl) => 55 + (Math.max(1, Number(lvl) || 1) - 1) * 5;

export const getNaturalStats = (lvl) => ({
    [AP_ID]: lvl >= 100 ? 7 : 6,
    [MP_ID]: 3,
    [VITALITY_ID]: getBaseVitality(lvl),
    [WISDOM_ID]: 0,
    [STRENGTH_ID]: 0,
    [INTELLIGENCE_ID]: 0,
    [CHANCE_ID]: 0,
    [AGILITY_ID]: 0,
    [PROSPECTING_ID]: 100,
    [PODS_ID]: 1000,
    [SP_ID]: 1,
});

// Get stat IDs from STATS for robust referencing

export const getCost = (statId, value) => {
    if (statId === VITALITY_ID) return 1;
    if (statId === WISDOM_ID) return 3;

    if (value <= 100) return 1;
    if (value <= 200) return 2;
    if (value <= 300) return 3;
    return 4;
}

export const calculateTotalCost = (statId, value) => {
    if (statId === VITALITY_ID) return value; // 1 for 1
    if (statId === WISDOM_ID) return value * 3; // 3 for 1

    if (value <= 100) return value; // 1 for 1
    if (value <= 200) return 100 + (value - 100) * 2; // 2 for 1
    if (value <= 300) return 100 + 200 + (value - 200) * 3; // 3 for 1
    return 100 + 200 + 300 + (value - 300) * 4; // 4 for 1
};

export const calculateAttributes = (lvl, statId, baseValue, inputValue, availablePoints) => {
    const maxPoints = getTotalCharacterPoints(lvl);
    inputValue = Math.min(maxPoints, Math.max(0, inputValue));

    // Calculate refund
    let refundPoints = 0;
    for (let i = 0; i < baseValue; i++) {
        const pointCost = getCost(statId, i + 1);
        refundPoints += pointCost;
    }

    let remainingPoints = availablePoints + refundPoints;
    let assignedValue = 0;

    // Assign as many points as possible towards the input value
    while (assignedValue < inputValue) {
        const nextCost = getCost(statId, assignedValue + 1);
        if (remainingPoints >= nextCost) {
            remainingPoints -= nextCost;
            assignedValue++;
        } else {
            break;
        }
    }

    // If we couldn't assign even one point, return the base value
    if (assignedValue === 0 && inputValue > 0) {
        return { assignedValue: baseValue, remainingPoints: availablePoints };
    }

    return { assignedValue, remainingPoints };
};


// Convert invested points to stat value for a given statId and level
export function investedPointsToStatValue(lvl, statId, investedPoints) {
    let value = 0;
    let points = investedPoints;
    while (points > 0) {
        const cost = getCost(statId, value + 1);
        if (points >= cost) {
            points -= cost;
            value++;
        } else {
            break;
        }
    }
    return value;
}
